import { useEffect, useMemo, useRef } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useNavigate } from "@tanstack/react-router"

import {
	desktopNotificationsEnabled,
	parseNotificationSettings,
} from "@productlane/lib/src/browser"
import { queries } from "@productlane/zero/queries"

import { useBrowserNotifications } from "@/lib/hooks/useBrowserNotifications"
import { notificationText } from "./notification-presentation"
import { notificationClickPath } from "./target-to-path"
import { useUnreadNotifications } from "./use-notifications"

/** Browser popup for new notifications while the tab is in the background, behind one global switch. */
export function NotificationDesktopWatcher() {
	const navigate = useNavigate()
	const { showNotification } = useBrowserNotifications()
	const { rows } = useUnreadNotifications()
	const [preference] = useQuery(queries.notificationPreference())
	const enabled = useMemo(
		() =>
			desktopNotificationsEnabled(
				parseNotificationSettings(preference?.settings),
			),
		[preference?.settings],
	)
	const seen = useRef<Set<string> | null>(null)

	useEffect(() => {
		// Everything synced on first load is history, not news.
		if (seen.current === null) {
			seen.current = new Set(rows.map((r) => r.id))
			return
		}
		for (const row of rows) {
			if (seen.current.has(row.id)) continue
			seen.current.add(row.id)
			// showNotification checks the browser permission itself, at call time.
			if (!enabled || document.hasFocus()) continue
			const payload = row.payload as Record<string, unknown> | null
			// Live chat has its own watcher with sound; avoid a second popup.
			if (payload?.liveChat === true) continue
			const { title, body } = notificationText(row)
			showNotification({
				title,
				body,
				path: notificationClickPath(row),
				tag: `notification-${row.threadId ?? row.id}`,
				onNavigate: (path) => void navigate({ href: path }),
			})
		}
	}, [rows, enabled, showNotification, navigate])

	return null
}
